/**
 * Partes da barra lateral que mudam quando ela recolhe na mesa (E205.F1.T05, ADR-0147 §8): a marca,
 * o botão que recolhe e expande, e o bloco da conta (carteira, identidade e "Sair"). Recolhida, a
 * barra mostra só os ícones a partir de `md`; os nomes seguem para o leitor de tela (`md:sr-only`).
 * Abaixo de `md` a barra é a faixa do topo de sempre. E207.F5.T01 (owner, 01/11): recolhida, cada
 * parte se centra no eixo do trilho, como os itens (`RECOLHIDA_NO_EIXO`).
 */
import { LogOut, PanelLeftClose, PanelLeftOpen } from 'lucide-react';
import { Link } from 'react-router-dom';

import { ptBR } from '@/i18n/pt-BR';
import { cn } from '@/lib/utils';

import { BancoAtivoSelector } from './BancoAtivoSelector';
import type { BarraDaMesa } from './barraRecolhida';
import { BrandMark, BrandWordmark } from './BrandMark';

/** E207.F5.T01: recolhida, cada parte se centra no eixo do trilho (o mesmo dos itens). */
const NO_EIXO = 'md:justify-center md:px-0';

export function MarcaDaBarra({ recolhida }: { recolhida: boolean }) {
  return (
    <Link
      to="/casos"
      aria-label={ptBR.productName}
      className={cn(
        'flex items-center gap-2.5 px-1.5 focus-visible:outline-2 focus-visible:outline-accent',
        recolhida && NO_EIXO,
      )}
      data-testid="sidebar-marca"
    >
      <BrandMark />
      {/* E163.F3 (A67-18): a marca do molde, sem "Forense · v0.1". */}
      <span className={recolhida ? 'md:hidden' : undefined}>
        <BrandWordmark eyebrow={ptBR.shell.brandKicker} />
      </span>
    </Link>
  );
}

/** O botão que recolhe e expande a barra, só na mesa. */
export function AlternarABarra({ barra }: { barra: BarraDaMesa }) {
  const rotulo = barra.recolhida ? ptBR.shell.sidebar.expandir : ptBR.shell.sidebar.recolher;
  const Icone = barra.recolhida ? PanelLeftOpen : PanelLeftClose;
  return (
    <button
      type="button"
      onClick={barra.alternar}
      aria-label={rotulo}
      aria-expanded={!barra.recolhida}
      aria-controls="app-sidebar"
      title={rotulo}
      data-testid="sidebar-alternar"
      className={cn(
        'hidden items-center gap-2.5 border border-transparent px-2.5 py-[7px] text-[13px] text-fg-muted transition-colors duration-fast ease-sc hover:bg-fg/[0.05] hover:text-fg focus-visible:outline-2 focus-visible:outline-accent md:flex',
        barra.recolhida && NO_EIXO,
      )}
    >
      <Icone className="h-[17px] w-[17px] shrink-0" aria-hidden="true" />
      <span className={barra.recolhida ? 'sr-only' : undefined}>{rotulo}</span>
    </button>
  );
}

export interface ContaDaBarraProps {
  recolhida: boolean;
  bancoAtivo: string | null;
  iniciais: string;
  displayName: string | null;
  /** O e-mail como linha secundária, quando há nome; `null` sem a linha. */
  email: string | null;
  /** Sair da sessão; `null` sem sessão (o botão não aparece). */
  onSair: (() => void) | null;
}

export function ContaDaBarra(props: ContaDaBarraProps) {
  const { recolhida, displayName, email, onSair } = props;
  return (
    <div className="mt-auto flex flex-col gap-2 border-t border-border pt-3.5">
      <div className={recolhida ? 'md:hidden' : undefined}>
        <BancoAtivoSelector bancoAtivo={props.bancoAtivo} />
      </div>
      <div
        className={cn('flex items-center gap-2.5 px-1 py-1.5', recolhida && NO_EIXO)}
        data-testid="sidebar-conta"
      >
        <div className="grid h-7 w-7 place-items-center border border-border-strong bg-surface-3 font-mono text-[11px] font-semibold text-fg">
          {props.iniciais}
        </div>
        <div
          className={cn('flex min-w-0 flex-1 flex-col leading-tight', recolhida && 'md:sr-only')}
        >
          <span
            className="truncate text-[12.5px] text-fg"
            title={displayName ?? undefined}
            data-testid="sidebar-account-name"
          >
            {displayName ?? ptBR.shell.sidebar.userAnonymous}
          </span>
          {email ? (
            <span className="truncate font-mono text-[10px] text-fg-muted" title={email}>
              {email}
            </span>
          ) : null}
        </div>
      </div>

      {onSair !== null ? (
        <button
          type="button"
          onClick={onSair}
          data-testid="sidebar-logout"
          aria-label={ptBR.nav.logout}
          className={cn(
            'inline-flex items-center justify-center gap-2 border border-border bg-transparent px-2.5 py-1.5 text-[12.5px] text-fg-muted transition-colors duration-fast ease-sc hover:bg-fg/[0.07] hover:text-fg focus-visible:outline-2 focus-visible:outline-accent',
            recolhida && NO_EIXO,
          )}
        >
          <LogOut className="h-3.5 w-3.5" aria-hidden="true" />
          <span className={recolhida ? 'md:sr-only' : undefined}>{ptBR.nav.logout}</span>
        </button>
      ) : null}
    </div>
  );
}
